/**
 * Чип одного дня недельной полосы — «два разреза» (задача 71 §5.1).
 *
 * Состояния из макапа M6/D6 «SPEC: TWO CUTS» (`DayChipState`):
 *  - `pending`  ▣N — сданные на подтверждении (очередь действий мастера);
 *  - `missing`  ∅N — люди по графику без отчёта;
 *  - `both`     ▣N + ∅N — горят оба разреза;
 *  - `all_clear` ✓ — день разобран;
 *  - `no_data`  — — нет ожидаемых и нет сдач.
 *
 * Цвет несёт смысл через tone-токены (`--tone-*`): pending = caution (жёлтая
 * «каска»), missing = danger, all_clear = success. Это форма tone-палитры в
 * духе `<NumericChip>` (mono-число подсвечено по смыслу), но локальная для
 * полосы — поэтому свой компонент, а не общий примитив (флаг в Результате).
 */
import { useTranslation } from "react-i18next";
import { getDay, parseISO } from "date-fns";

import { cn } from "@/lib/utils";
import type { WeekDaySummary } from "@/api/approval";

interface DayChipProps {
  day: WeekDaySummary;
  dow: string;
  dayNum: string;
  density: "desktop" | "mobile";
  selected: boolean;
  onSelect: () => void;
}

export function DayChip({
  day,
  dow,
  dayNum,
  density,
  selected,
  onSelect,
}: DayChipProps): React.ReactElement {
  const { t } = useTranslation();
  const showCuts = day.state === "pending" || day.state === "missing" || day.state === "both";

  // Выходной (сб/вс) выводим из даты на фронте — флага в payload нет. На
  // desktop его кодируют neutral-поверхность и danger-шапка (§1.12); mobile-M6
  // пока сохраняет прежнюю danger-рамку. Выбранный день важнее — у него ring.
  const dow0 = getDay(parseISO(day.date));
  const isWeekend = dow0 === 0 || dow0 === 6;
  const useUnifiedDesktopHeader = density === "desktop";

  // Доступное имя кнопки: SR озвучивает день + разрезы одной фразой (иначе читал
  // бы голые глифы ▣/∅ и числа вразнобой). aria-label на <button> перекрывает
  // внутренний текст как accessible name.
  const ariaLabel = (() => {
    const head = `${dow} ${dayNum}`;
    if (day.state === "all_clear") return `${head}, ${t("timeApproval.chipDoneAria")}`;
    if (day.state === "no_data") return `${head}, ${t("timeApproval.chipNoDataAria")}`;
    const cuts: string[] = [];
    if (day.state === "pending" || day.state === "both") {
      cuts.push(t("timeApproval.ctxPending", { count: day.pending_count }));
    }
    if (day.state === "missing" || day.state === "both") {
      cuts.push(t("timeApproval.ctxMissing", { count: day.missing_count }));
    }
    return `${head}, ${cuts.join(", ")}`;
  })();

  return (
    <button
      type="button"
      data-testid={`approval-day-${day.date}`}
      data-state={day.state}
      aria-pressed={selected}
      aria-label={ariaLabel}
      onClick={onSelect}
      className={cn(
        "flex min-w-0 flex-1 flex-col items-stretch gap-1.5 rounded-[var(--radius-card)] px-2 py-2 text-left transition",
        "border bg-[var(--bg-panel-0)]",
        selected
          ? "ring-foreground border-transparent ring-2"
          : "hover:border-[var(--rule-strong)]",
      )}
      data-weekend={isWeekend ? "" : undefined}
      style={{
        borderColor: selected
          ? undefined
          : isWeekend && useUnifiedDesktopHeader
            ? "var(--tone-neutral-border)"
            : isWeekend
              ? "var(--tone-danger-border)"
              : "var(--rule-line)",
        background:
          isWeekend && useUnifiedDesktopHeader ? "var(--bg-panel-1)" : "var(--bg-panel-0)",
        // Прежний mobile-M6 сохраняет усиленную рамку выходного. На desktop
        // weekend теперь кодируют neutral-поверхность и danger-шапка (§1.12).
        borderWidth: isWeekend && !selected && !useUnifiedDesktopHeader ? "2px" : undefined,
      }}
    >
      <span
        className={cn(
          "flex items-baseline gap-1",
          useUnifiedDesktopHeader ? "justify-end" : "justify-between",
        )}
      >
        <span
          className="font-condensed text-xs uppercase tracking-[0.06em]"
          style={{
            color: isWeekend && useUnifiedDesktopHeader ? "var(--tone-danger-fg)" : "var(--fg-3)",
          }}
        >
          {dow}
        </span>
        <span
          className="font-mono text-sm font-semibold tabular-nums"
          style={{
            color: isWeekend && useUnifiedDesktopHeader ? "var(--tone-danger-fg)" : "var(--fg-1)",
          }}
        >
          {dayNum}
        </span>
      </span>

      {showCuts ? (
        <span className="flex flex-wrap items-center gap-1">
          {(day.state === "pending" || day.state === "both") && (
            <Cut tone="caution" glyph="▣" count={day.pending_count} />
          )}
          {(day.state === "missing" || day.state === "both") && (
            <Cut tone="danger" glyph="∅" count={day.missing_count} />
          )}
        </span>
      ) : day.state === "all_clear" ? (
        <span
          className="flex items-center gap-1 text-xs font-medium tabular-nums"
          style={{ color: "var(--tone-success-fg)" }}
          aria-hidden="true"
        >
          <span>✓</span>
          <span>
            {day.approved_count}/{day.expected_count}
          </span>
        </span>
      ) : (
        <span className="text-xs text-[var(--fg-4)]" aria-hidden="true">
          —
        </span>
      )}
    </button>
  );
}

function Cut({
  tone,
  glyph,
  count,
}: {
  tone: "caution" | "danger";
  glyph: string;
  count: number;
}): React.ReactElement {
  return (
    <span
      className="inline-flex items-center gap-0.5 rounded-[var(--radius-chip)] px-1 py-0.5 text-xs font-semibold tabular-nums"
      style={{
        background: `var(--tone-${tone}-bg)`,
        color: `var(--tone-${tone}-fg)`,
      }}
    >
      <span aria-hidden="true">{glyph}</span>
      <span>{count}</span>
    </span>
  );
}
